import React, { useState } from 'react';
import type { Agency, ExportJob } from '@atlas/shared';
import { slugifyCompany } from '@atlas/shared';
import { createExport } from '../lib/callables.js';
import { confettiEverywhere } from '../lib/confetti.js';
import { AgencyPicker } from './AgencyPicker.js';

interface NewExportFormProps {
  onCreated?: (exportId: string) => void;
  /** Existing exports, used to warn early on a company-folder collision. */
  existingExports?: Array<ExportJob & { id: string }>;
}

export function NewExportForm({ onCreated, existingExports = [] }: NewExportFormProps) {
  const [agency, setAgency] = useState<Agency | null>(null);
  const [companyName, setCompanyName] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const slug = slugifyCompany(companyName);

  // A company slug collides only with another export still queued/running.
  const nameTaken =
    Boolean(slug) &&
    existingExports.some(
      (e) =>
        e.companySlug === slug &&
        (e.status === 'queued' || e.status === 'running'),
    );

  function handleSelectAgency(next: Agency | null) {
    setAgency(next);
    // Pre-fill the company name from the agency name on first pick; the
    // engineer can still edit it (it drives the S3 folder + SFTP username).
    if (next && !companyName.trim()) {
      setCompanyName(next.name);
    }
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!agency) {
      setError('Choose an agency to export.');
      return;
    }
    if (!slug) {
      setError('Company name must contain at least one letter or number.');
      return;
    }
    if (nameTaken) {
      setError('An export for this company is already in progress.');
      return;
    }
    setError(null);
    setLoading(true);
    try {
      const result = await createExport({
        agencyId: agency.id,
        companyName: companyName.trim(),
      });
      confettiEverywhere();
      setAgency(null);
      setCompanyName('');
      onCreated?.(result.data.exportId);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setLoading(false);
    }
  }

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-4">
      <label className="flex flex-col gap-1">
        <span className="text-xs font-medium text-[#73726e]">Agency</span>
        <AgencyPicker value={agency} onChange={handleSelectAgency} />
        {agency && (
          <span className="text-xs text-[#aeaaa4]">
            Agency ID <code className="font-mono">{agency.id}</code>
          </span>
        )}
      </label>

      <label className="flex flex-col gap-1">
        <span className="text-xs font-medium text-[#73726e]">
          Company name (folder + SFTP username)
        </span>
        <input
          required
          className={`px-2.5 py-1.5 text-sm border rounded focus:outline-none focus:ring-1 text-[#37352f] placeholder:text-[#aeaaa4] ${
            nameTaken
              ? 'border-[#e03e3e] focus:ring-[#e03e3e]'
              : 'border-[#e9e9e7] focus:ring-[#2383e2]'
          }`}
          placeholder="e.g. ryton"
          value={companyName}
          onChange={(e) => setCompanyName(e.target.value)}
        />
        {slug && !nameTaken && (
          <span className="text-xs text-[#aeaaa4]">
            Files land in <code className="font-mono">{slug}/</code> and the SFTP
            username will be <code className="font-mono">{slug}</code>.
          </span>
        )}
        {nameTaken && (
          <span className="text-xs text-[#e03e3e]">
            An export for this company is already in progress.
          </span>
        )}
      </label>

      {error && (
        <p className="text-sm text-[#e03e3e] bg-[#fbe8e8] border border-[#f5c6c6] rounded px-3 py-2">
          {error}
        </p>
      )}

      <div className="flex justify-end pt-1">
        <button
          type="submit"
          disabled={loading || nameTaken || !agency}
          className="px-4 py-1.5 text-sm font-medium text-white bg-[#37352f] rounded hover:bg-[#2e2e2c] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {loading ? 'Starting…' : 'Start export'}
        </button>
      </div>
    </form>
  );
}
